import { Form } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import { SUCCESS, WAREHOUSES_REGISTRY_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type { AddWarehouseForm } from '@app/pages/Warehouses/WarehouseRegistry/components/CreateWarehouse/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import {
  useLazyGetWarehouseByIdQuery,
  useUpdateWarehouseMutation,
} from '@app/services/rtkQuery/Warehouses/api';
import type { ModelWarehouse } from '@app/services/rtkQuery/Warehouses/types';
import type { NBranches } from '@app/types';

const { useForm } = Form;
export const useCreateWarehouse = () => {
  const [branchError, setBranchError] = useState<string | null>(null);
  const [openBranchesModal, setOpenBranchesModal] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const [allBranches, setAllBranches] = useState<
    NBranches.ModelBranchItem[] | null
  >(null);
  const [editableWarehouse, setEditableWarehouse] =
    useState<ModelWarehouse | null>(null);
  const [isOpenDeleteWarehouseModal, setIsOpenDeleteWarehouseModal] =
    useState(false);

  const [searchParams] = useSearchParams();
  const warehouseId = useMemo(
    () => (searchParams.get('id') ? Number(searchParams.get('id')) : null),
    [searchParams]
  );

  const { activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const [form] = useForm<AddWarehouseForm>();

  const navigate = useNavigate();

  const [updateWarehouse, { isLoading: isWarehouseUpdating }] =
    useUpdateWarehouseMutation();
  const [getWarehouseById, { isLoading: isGetWarehouseLoading }] =
    useLazyGetWarehouseByIdQuery();
  const {
    userAccessRights: {
      Warehouse: { IsWarehouseCreate, IsWarehouseEdit, IsWarehouseDelete },
    },
  } = useNewUserAccessRights();

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();

  const isPageForbidden = useMemo(() => {
    if (warehouseId) {
      return !IsWarehouseEdit;
    }
    return !IsWarehouseCreate;
  }, [IsWarehouseCreate, IsWarehouseEdit, warehouseId]);

  const isPageLoading = useMemo(() => {
    return isGetWarehouseLoading || !allBranches;
  }, [isGetWarehouseLoading, allBranches]);

  const showBranchesModal = useCallback(() => {
    setOpenBranchesModal(true);
  }, []);

  const hideBranchesModal = useCallback(() => {
    setOpenBranchesModal(false);
  }, []);

  const handleGetBranches = useCallback(async () => {
    if (isPageForbidden) {
      return;
    }
    try {
      const response = await getBranchesForSystem({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setAllBranches(response);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [isPageForbidden, getBranchesForSystem, activeBranchId, showNotification]);

  const handleGetWarehouse = useCallback(async () => {
    if (isPageForbidden) {
      return;
    }
    if (warehouseId) {
      try {
        const result = await getWarehouseById({
          ActiveBranchId: activeBranchId,
          warehouseId,
        }).unwrap();

        setEditableWarehouse(result);

        const selectedBranches = allBranches?.filter(({ Id }) =>
          result.BranchIds?.includes(Id)
        );

        setSelectedBranches(selectedBranches || []);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    }
  }, [
    activeBranchId,
    allBranches,
    getWarehouseById,
    showNotification,
    warehouseId,
    isPageForbidden,
  ]);

  const navigateToRegistry = useCallback(() => {
    navigate(WAREHOUSES_REGISTRY_PATH);
  }, [navigate]);

  const handleSaveWarehouse = useCallback(
    async (values: AddWarehouseForm) => {
      if (selectedBranches.length === 0) {
        setBranchError('Необходимо выбрать хотя бы один филиал');
        return;
      }

      if (isPageForbidden) {
        return;
      }

      try {
        const { Name, Comment } = values;
        const data = {
          Id: editableWarehouse ? editableWarehouse.Id : 0,
          Name,
          Comment: Comment || null,
          IsDeleted: editableWarehouse ? editableWarehouse.IsDeleted : false,
          BranchIds: selectedBranches.map(({ Id }) => {
            return Id;
          }),
          CountProducts: editableWarehouse
            ? editableWarehouse.CountProducts
            : 0,
        };

        await updateWarehouse({
          ActiveBranchId: activeBranchId,
          data,
        }).unwrap();

        navigateToRegistry();

        const successMessage = editableWarehouse
          ? 'Склад обновлен успешно'
          : 'Склад создан успешно';

        showNotification(successMessage, SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      editableWarehouse,
      navigateToRegistry,
      selectedBranches,
      showNotification,
      updateWarehouse,
      isPageForbidden,
    ]
  );

  const deleteWarehouseHandler = useCallback(async () => {
    if (isPageForbidden) {
      return;
    }

    if (!editableWarehouse) {
      return;
    }

    try {
      const data: ModelWarehouse = {
        ...editableWarehouse,
        Id: editableWarehouse ? editableWarehouse.Id : 0,
        IsDeleted: true,
      };

      await updateWarehouse({
        ActiveBranchId: activeBranchId,
        data,
      }).unwrap();

      navigateToRegistry();

      showNotification('Склад удален успешно', SUCCESS);
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    editableWarehouse,
    isPageForbidden,
    navigateToRegistry,
    updateWarehouse,
    showNotification,
  ]);

  const checkBranchSelection = useCallback(
    (checkedBranches?: NBranches.ModelBranchItem[]) => {
      const branches = checkedBranches || selectedBranches;
      const errorMessage = 'Необходимо выбрать хотя бы один филиал';

      setBranchError(branches.length === 0 ? errorMessage : null);
    },
    [selectedBranches]
  );

  const showDeleteButton = useMemo(() => {
    return (
      editableWarehouse !== null &&
      IsWarehouseDelete &&
      !editableWarehouse?.IsDeleted
    );
  }, [editableWarehouse, IsWarehouseDelete]);

  const openDeleteWarehouseModal = useCallback(() => {
    setIsOpenDeleteWarehouseModal(true);
  }, []);

  return {
    form,
    openBranchesModal,
    showBranchesModal,
    hideBranchesModal,
    selectedBranches,
    setSelectedBranches,
    allBranches,
    handleGetBranches,
    handleSaveWarehouse,
    isWarehouseUpdating,
    navigateToRegistry,
    handleGetWarehouse,
    isPageForbidden,
    isPageLoading,
    checkBranchSelection,
    branchError,
    showDeleteButton,
    openDeleteWarehouseModal,
    isOpenDeleteWarehouseModal,
    setIsOpenDeleteWarehouseModal,
    deleteWarehouseHandler,
    editableWarehouse,
  };
};
